import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Button,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  HelpOutline,
  VolunteerActivism,
  AdminPanelSettings,
  PersonAdd,
  Event,
  Schedule,
  Notifications,
  Person,
  CheckCircle,
  Campaign,
  Group,
  Analytics,
  Edit,
  Delete,
  Repeat,
  Star,
  Email,
  PhoneAndroid,
  Login,
  Lock,
  ArrowForward,
  BookmarkAdd,
  BookmarkAdded,
  Forum,
  Link as LinkIcon,
  PhoneIphone,
  Settings as SettingsIcon,
  DoneAll,
  Cancel as CancelIcon,
  NotificationsActive,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';

// Numbered step circle
const Step = ({ n, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: 14,
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      {n}
    </Box>
    <Box sx={{ pt: 0.25 }}>{children}</Box>
  </Box>
);

// Section header with icon
const SectionHeader = ({ icon, title, subtitle }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
    <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="h6" fontWeight="bold">{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      )}
    </Box>
  </Box>
);

// FAQ accordion item
const Faq = ({ question, children }) => (
  <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography fontWeight="medium">{question}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2" color="text.secondary" component="div">
        {children}
      </Typography>
    </AccordionDetails>
  </Accordion>
);

// ─── Volunteer content ────────────────────────────────────────────────────────
const VolunteerHelp = () => (
  <Box>
    {/* Getting Started */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<PersonAdd />} title="Getting Started" subtitle="How to create an account and join an organization" />
      <Step n={1}>
        <Typography variant="body2"><strong>Create an account</strong> — Click <em>Sign Up</em> on the home page. Choose <em>Volunteer</em> as your account type and fill in your name, email, and a password.</Typography>
      </Step>
      <Step n={2}>
        <Typography variant="body2"><strong>Enter an invite code</strong> — Your organization admin will give you an invite code. Enter it during registration to link your account to the organization.</Typography>
      </Step>
      <Step n={3}>
        <Typography variant="body2"><strong>Wait for approval</strong> — An admin reviews new volunteers before you can sign up for events. You'll see a <em>Pending</em> status badge on your profile until approved. You'll receive an email when you're approved (or if your application is not accepted).</Typography>
      </Step>
      <Step n={4}>
        <Typography variant="body2"><strong>You're in!</strong> — Once approved, your status changes to <em>Active</em> and you can start signing up for events from the Dashboard.</Typography>
      </Step>
    </Paper>

    {/* Signing Up for Events */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Event />} title="Signing Up for Events" subtitle="Browse upcoming events and choose your role" />
      <Step n={1}>
        <Typography variant="body2"><strong>Go to your Dashboard</strong> — All upcoming events for your organization are listed here.</Typography>
      </Step>
      <Step n={2}>
        <Typography variant="body2"><strong>Click an event</strong> — View the date, time, location, and available roles (called <em>Assignments</em>). Each assignment shows how many spots are still open.</Typography>
      </Step>
      <Step n={3}>
        <Typography variant="body2"><strong>Click Sign Up</strong> — Pick the assignment you want and confirm. You'll be registered immediately.</Typography>
      </Step>
      <Step n={4}>
        <Typography variant="body2"><strong>Recurring events</strong> — If an event repeats (weekly, biweekly, etc.), you can sign up for just one occurrence or the entire series at once using the option in the sign-up dialog.</Typography>
      </Step>
      <Alert severity="info" sx={{ mt: 1, fontSize: 13 }}>
        You'll receive a reminder email 5 days before the event and again the day before. Enable SMS reminders in your Profile for text message alerts too.
      </Alert>
    </Paper>

    {/* My Schedule */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Schedule />} title="My Schedule" subtitle="View and manage your registered events" />
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Click My Schedule in the top navigation to see all events you're registered for." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary='To cancel a registration, find the event on your Schedule page and click "Cancel Registration".' primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="You can optionally enter a reason for cancelling. The reason is visible to org admins and helps them plan accordingly." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="For recurring events, you can cancel just the one occurrence or all upcoming events in the series." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Past events remain visible with an Attended or Cancelled status for your records." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </List>
    </Paper>

    {/* Profile & Notifications */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Person />} title="Your Profile & Settings" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <PhoneAndroid color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">SMS Reminders</Typography>
              <Typography variant="body2" color="text.secondary">Add your phone number in Profile, then toggle <em>SMS Reminders</em> on. You'll get a text 5 days before and again the day before each event.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Lock color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Change Password</Typography>
              <Typography variant="body2" color="text.secondary">Go to Profile and click <em>Change Password</em>. Passwords must be at least 8 characters with uppercase, lowercase, and a number.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Group color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Multiple Organizations</Typography>
              <Typography variant="body2" color="text.secondary">You can belong to more than one organization. Use <em>Join Another Organization</em> in your Profile and enter a new invite code.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Star color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Default Organization</Typography>
              <Typography variant="body2" color="text.secondary">If you're in multiple organizations, click the star icon next to an org in your Profile to set it as your default on login.</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>

    {/* Install on Mobile */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<PhoneIphone />} title="Install on Your Phone" subtitle="Add ServeSync to your home screen for quick access and push notifications" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <PhoneIphone color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">iPhone / iPad (Safari)</Typography>
              <Typography variant="body2" color="text.secondary">Open ServeSync in <strong>Safari</strong>, tap the Share button (rectangle with arrow) at the bottom, then tap <em>"Add to Home Screen"</em>. ServeSync will appear as an app icon on your home screen.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <PhoneIphone color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Android (Chrome)</Typography>
              <Typography variant="body2" color="text.secondary">Tap the three-dot menu in Chrome and select <em>"Add to Home screen"</em> or <em>"Install app"</em>. You may also see an install prompt appear automatically in the address bar.</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
        Once installed, ServeSync works like a native app — it opens full screen without the browser bar and loads faster on repeat visits.
      </Alert>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <NotificationsActive color="primary" fontSize="small" />
          <Typography variant="body2" fontWeight="bold">Enabling Push Notifications</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Push notifications let ServeSync alert you about event reminders, volunteer approvals, and org announcements — even when the app isn't open.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" color="primary" fontWeight="bold" sx={{ minWidth: 20 }}>1.</Typography>
              <Typography variant="body2" color="text.secondary"><strong>Install the app first</strong> — On iPhone, push notifications only work when ServeSync is added to your home screen and opened from there. They do not work in the Safari browser tab.</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" color="primary" fontWeight="bold" sx={{ minWidth: 20 }}>2.</Typography>
              <Typography variant="body2" color="text.secondary"><strong>Turn on the toggle</strong> — Go to your <strong>Profile</strong> and enable the <em>Push Notifications</em> toggle. Your browser will ask for permission — tap <em>Allow</em>.</Typography>
            </Box>
          </Grid>
        </Grid>
        <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
          <strong>iPhone users:</strong> You must add ServeSync to your home screen from Safari <em>before</em> enabling push notifications. The toggle will show "Not supported in this browser" if you haven't done this yet.
        </Alert>
      </Box>
    </Paper>

    {/* FAQ */}
    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Frequently Asked Questions</Typography>
    <Faq question="I signed up but my account says Pending — what does that mean?">
      Your organization admin needs to approve new volunteers before you can register for events. This usually happens within a day or two. If it's been a while, contact your organization admin directly.
    </Faq>
    <Faq question="I didn't get my reminder email or SMS — what should I check?">
      Check your spam folder for emails. For SMS, make sure your phone number in your Profile includes the country code (e.g., +1 for US) and that <em>SMS Reminders</em> is toggled on. You'll receive a confirmation SMS when you first enable it.
    </Faq>
    <Faq question="I'm receiving duplicate push notifications — how do I fix it?">
      This can happen if you've added ServeSync to your home screen more than once, reinstalled the app, or if your device re-registered for push notifications after an update. Each installation registers a separate notification channel, so you may temporarily receive the same alert twice.
      <br /><br />
      To fix it, go to your <strong>Profile</strong>, turn off <em>Push Notifications</em>, then turn them back on. This resets your device registration to a single channel and stops the duplicates.
    </Faq>
    <Faq question="Can I sign up for the same event more than once?">
      You can register for multiple different assignments within the same event, but not the same assignment twice.
    </Faq>
    <Faq question="I forgot my password — how do I reset it?">
      Click <em>Forgot Password</em> on the login page and enter your email. You'll receive a reset link. If the email goes to spam, check there. Make sure to use the link before it expires (usually within 1 hour).
    </Faq>
    <Faq question="How do I leave an organization?">
      Contact your organization admin to have your account removed. This feature is managed by admins to preserve volunteer history.
    </Faq>
  </Box>
);

// ─── Org Admin content ────────────────────────────────────────────────────────
const OrgAdminHelp = () => (
  <Box>
    {/* Setup */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<AdminPanelSettings />} title="Setting Up Your Organization" subtitle="First steps after creating your admin account" />
      <Step n={1}>
        <Typography variant="body2"><strong>Register as Organization Admin</strong> — Click <em>Sign Up</em>, choose <em>Organization Admin</em>, and fill in both your personal info and your organization's details (name, description, address, contact info).</Typography>
      </Step>
      <Step n={2}>
        <Typography variant="body2"><strong>Find your invite code</strong> — After registration, go to your Dashboard. Your organization's invite code is displayed there. Share it with volunteers so they can join.</Typography>
      </Step>
      <Step n={3}>
        <Typography variant="body2"><strong>Approve volunteers</strong> — When a volunteer joins with your invite code, they appear as <em>Pending</em> in your Dashboard. Review and approve or reject them.</Typography>
      </Step>
      <Step n={4}>
        <Typography variant="body2"><strong>Create your first event</strong> — Click <em>Create Event</em> in the navigation or Dashboard to start scheduling volunteer opportunities.</Typography>
      </Step>
    </Paper>

    {/* Managing Volunteers */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Group />} title="Managing Volunteers" subtitle="Approve, assign, and remove volunteers" />
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Approve/reject:</strong> Pending volunteers appear on your Dashboard and the Volunteers page. Click Approve or Reject on individual volunteers. The volunteer automatically receives an email when approved or rejected.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><DoneAll color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Bulk approve/reject:</strong> On the Volunteers page, filter by <em>Pending</em>. Use the <em>Select All</em> button (or check individual boxes) and then click <em>Approve All</em> or <em>Reject All</em> to process multiple volunteers at once.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Manually assign:</strong> Open any event and click <em>Add Volunteer</em> to assign a specific volunteer to a specific assignment slot.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><PersonAdd color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Add someone who won't sign up themselves:</strong> On the Volunteers page, click <em>Add Volunteer</em> and enter their name, email, and (optional) phone number — no account required. They'll appear in event assignment dropdowns tagged <em>pending signup</em>, get email reminders for events they're registered for, and can later create an account to unlock SMS and push reminders.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Remove a volunteer:</strong> On the event detail page, click the remove (person minus) icon next to a volunteer's name in the registered list.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<><strong>Mark attendance:</strong> After an event, update volunteer attendance status from the event detail page.</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>
    </Paper>

    {/* Creating Events */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Event />} title="Creating Events" subtitle="Single events and recurring series" />
      <Step n={1}>
        <Typography variant="body2"><strong>Click Create Event</strong> in the navigation bar or Dashboard.</Typography>
      </Step>
      <Step n={2}>
        <Typography variant="body2"><strong>Fill in the details</strong> — Title, description, date, start/end time, and location are required.</Typography>
      </Step>
      <Step n={3}>
        <Typography variant="body2"><strong>Add assignments</strong> — Each assignment is a role volunteers can sign up for (e.g., "Greeter — 2 spots", "Setup Crew — 4 spots"). You can have multiple assignments per event.</Typography>
      </Step>
      <Step n={4}>
        <Typography variant="body2"><strong>Recurring events (optional)</strong> — Toggle <em>Recurring Event</em> to create a series. Choose a pattern (weekly, biweekly, monthly, or monthly by weekday) and an end date. The system generates all instances automatically.</Typography>
      </Step>
      <Step n={5}>
        <Typography variant="body2"><strong>Notify volunteers</strong> — Keep the <em>Notify all volunteers by email</em> checkbox checked (it's on by default) to automatically send an announcement email to all active volunteers when you save.</Typography>
      </Step>
    </Paper>

    {/* Editing Events */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Edit />} title="Editing & Deleting Events" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Edit color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Edit a single event</Typography>
              <Typography variant="body2" color="text.secondary">Open the event and click <em>Edit Event</em>. Change any field and save. Only this occurrence is updated.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Repeat color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Edit entire series</Typography>
              <Typography variant="body2" color="text.secondary">On the Edit page for any recurring event, choose <em>Edit all events in series</em>. Shared fields (title, location, times, assignments) update across all instances while each event keeps its own date.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Delete color="error" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Delete an event</Typography>
              <Typography variant="body2" color="text.secondary">Click <em>Delete</em> on the event page. For recurring events, choose to delete just this occurrence or the entire series. Deletion cannot be undone.</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Campaign color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Re-notify volunteers</Typography>
              <Typography variant="body2" color="text.secondary">Use the <em>Notify Volunteers</em> button on any event page to resend the announcement email to all active org volunteers at any time.</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>

    {/* Reminders & Emails */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Notifications />} title="Automatic Reminders" subtitle="How the system keeps volunteers informed" />
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><Email color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Email + SMS sent 5 days before each event to all registered volunteers." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><Email color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Email + SMS sent the day before the event as a final reminder." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><Campaign color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText primary='Announcement emails are sent when you create an event (if "Notify volunteers" is checked) or manually via the Notify Volunteers button.' primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><PhoneAndroid color="primary" fontSize="small" /></ListItemIcon>
          <ListItemText primary="SMS reminders only go to volunteers who have added a phone number and enabled SMS in their profile." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </List>
    </Paper>

    {/* Co-Admins */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<AdminPanelSettings />} title="Co-Admin Management" subtitle="Share admin responsibilities with trusted volunteers" />
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={<>Go to <strong>Profile → Co-Admins</strong> section (visible only to the org owner).</>}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Select a volunteer from the dropdown and click Promote. They immediately gain org-admin access."
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="To remove a co-admin, click the remove icon next to their name. They return to volunteer status."
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Co-admins can create events, manage volunteers, and edit events — but only the owner can manage co-admins."
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      </List>
    </Paper>

    {/* Event Templates */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<BookmarkAdd />} title="Event Templates" subtitle="Save and reuse event setups to create events faster" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Templates let you save the common details of an event — title, description, location, times, and assignments — so you don't have to re-enter them every time. The date and recurrence settings are intentionally excluded because those change with each event.
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Saving a template</Typography>
        <Step n={1}>
          <Typography variant="body2">Go to <strong>Create Event</strong> in the navigation.</Typography>
        </Step>
        <Step n={2}>
          <Typography variant="body2">Fill in the event details — title, description, location, start/end times, and assignments.</Typography>
        </Step>
        <Step n={3}>
          <Typography variant="body2">Click <strong>Save as Template</strong> (top right of the form). Enter a descriptive name like <em>"Food Drive Setup"</em> or <em>"Monthly Cleanup"</em> and click Save Template.</Typography>
        </Step>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>Loading a template</Typography>
        <Step n={1}>
          <Typography variant="body2">Go to <strong>Create Event</strong>. Once you have at least one saved template, a <strong>Load Template</strong> button appears next to Save as Template.</Typography>
        </Step>
        <Step n={2}>
          <Typography variant="body2">Click <strong>Load Template</strong> to open the template picker. Each template shows its name, event title preview, and assignment count.</Typography>
        </Step>
        <Step n={3}>
          <Typography variant="body2">Click any template to instantly pre-fill the form. Then just set the <strong>date</strong> (and recurrence if needed) and submit.</Typography>
        </Step>
      </Box>
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Templates are shared across all admins in your organization — co-admins can use them too." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="To delete a template, open Load Template, hover over the template, and click the trash icon." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
          <ListItemText primary="Loading a template doesn't create an event — it just pre-fills the form. You can still edit any field before submitting." primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </List>
    </Paper>

    {/* Communications */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Campaign />} title="Communications" subtitle="Send announcements and manage community links" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The <strong>Communications</strong> page (available in the top navigation for admins) is your central hub for messaging volunteers and sharing links to your community spaces.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Forum color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight="bold">Announcements</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Announcements appear as a live feed on the Dashboard for all active org members. You can optionally send each announcement as an email blast at the same time.
        </Typography>
        <Step n={1}>
          <Typography variant="body2">Go to <strong>Communications</strong> in the navigation bar.</Typography>
        </Step>
        <Step n={2}>
          <Typography variant="body2">Enter a <strong>Title</strong> and <strong>Message</strong> in the compose form.</Typography>
        </Step>
        <Step n={3}>
          <Typography variant="body2">Toggle <strong>"Also send by email to all members"</strong> if you want to email the announcement to every active volunteer and admin in your org at the same time.</Typography>
        </Step>
        <Step n={4}>
          <Typography variant="body2">Click <strong>Post Announcement</strong>. The message instantly appears in the announcements feed on the Dashboard for all members and, if email was selected, sends immediately.</Typography>
        </Step>
        <Alert severity="info" sx={{ mt: 1, mb: 2, fontSize: 13 }}>
          The announcement history on the Communications page shows every past announcement along with who sent it, when, and how many emails were delivered.
        </Alert>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <LinkIcon color="primary" fontSize="small" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Typography variant="body2" fontWeight="bold">Community Links</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Add links to your Signal group, GroupMe, Discord server, WhatsApp group, Slack workspace, or any other community space. These links appear as clickable chips on the Dashboard so all volunteers can find them easily.
        </Typography>
        <List dense disablePadding>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={<>On the <strong>Communications</strong> page, click the <strong>+</strong> button in the Community Links panel.</>}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary='Enter a name (e.g., "Signal Group"), the full URL, and an optional description. Click Add Link.'
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="ServeSync automatically detects the platform from the URL or name and shows the appropriate icon."
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="To remove a link, click the trash icon next to it in the Community Links panel."
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </ListItem>
        </List>
      </Box>
    </Paper>

    {/* Analytics */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<Analytics />} title="Analytics" subtitle="Track participation and volunteer hours" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The <strong>Analytics</strong> page (available in the top navigation for admins) shows:
      </Typography>
      <List dense disablePadding>
        <ListItem disableGutters sx={{ pl: 1 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Total volunteer hours logged across your organization" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters sx={{ pl: 1 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Event participation rates and attendance history" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters sx={{ pl: 1 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Individual volunteer contribution summaries" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </List>
    </Paper>

    {/* Org Settings */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<SettingsIcon />} title="Organization Settings" subtitle="Update your organization's profile and contact info" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Go to <strong>Settings</strong> in the navigation bar to edit your organization's details at any time. Changes take effect immediately.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <SettingsIcon color="primary" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">What you can edit</Typography>
              <Typography variant="body2" color="text.secondary">Organization name, description, website, contact email, contact phone, and your organization's address (street, city, state, ZIP).</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <CheckCircle color="success" fontSize="small" sx={{ mt: 0.25 }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">Co-admin access</Typography>
              <Typography variant="body2" color="text.secondary">Both the organization owner and co-admins can update organization settings.</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>

    {/* Cancellation Notifications */}
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      <SectionHeader icon={<CancelIcon />} title="Volunteer Cancellation Notifications" subtitle="Stay informed when volunteers cancel" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        When a volunteer cancels their registration, all org admins automatically receive an email notification with:
      </Typography>
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="The volunteer's name and email" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Which event and assignment they cancelled from" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="The reason they provided (if any)" primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 32 }}><ArrowForward fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary='A direct link to the event page so you can notify other volunteers about the open spot.' primaryTypographyProps={{ variant: 'body2' }} />
        </ListItem>
      </List>
    </Paper>

    {/* FAQ */}
    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Frequently Asked Questions</Typography>
    <Faq question="How do I find my organization's invite code?">
      Your invite code is shown on your Dashboard in the organization info section. You can share it directly with volunteers so they can join during registration.
    </Faq>
    <Faq question="Can I add a volunteer who won't sign up for an account themselves?">
      Yes. On the Volunteers page, click <em>Add Volunteer</em> and enter their name, email, and optional phone number. They show up in the Invited tab tagged "Added by admin" and can be assigned to events just like any other volunteer. They'll get email reminders for events they're registered for, but not SMS or push — those stay opt-in and only unlock once the person creates their own account and turns them on. If they later sign up with their email, their event history carries over automatically.
    </Faq>
    <Faq question="A volunteer signed up but I want to assign them to a different slot — can I do that?">
      Yes. Remove them from their current slot using the remove icon on the event page, then use <em>Add Volunteer</em> to assign them to the correct assignment.
    </Faq>
    <Faq question="Can I limit who sees or signs up for an event?">
      Events are visible to all active volunteers in your organization. All approved volunteers can sign up for open spots. Use assignment slot counts to control how many people fill each role.
    </Faq>
    <Faq question="What happens to registrations when I delete an event?">
      Deleting an event removes it from the system. Volunteers who were registered will no longer see it on their schedule. They are not automatically notified, so consider reaching out to them directly before deleting.
    </Faq>
    <Faq question="The spot count on my event shows differently than I expect — why?">
      The event page uses live registration counts for admins, so the number reflects real-time sign-ups rather than any cached value. If something looks off after edits, refresh the page.
    </Faq>
    <Faq question="Can I have multiple organizations?">
      Each admin account is linked to one organization at a time. If you need to manage multiple organizations, create a separate admin account for each. Volunteers, however, can belong to multiple organizations.
    </Faq>
    <Faq question="Can I edit a template after saving it?">
      Templates cannot be edited directly. To update one, load it into the Create Event form, make your changes, and save it as a new template with the same (or updated) name. Then delete the old version from the Load Template picker.
    </Faq>
    <Faq question="Do announcements send to pending volunteers?">
      No. Both in-app announcements and broadcast emails only reach volunteers with an <em>Active</em> status. Pending and rejected volunteers are excluded.
    </Faq>
    <Faq question="Can volunteers see the announcement history?">
      Yes. All active org members — volunteers and admins — see the five most recent announcements in the feed on their Dashboard. Org admins can view the full history on the Communications page.
    </Faq>
  </Box>
);

// ─── Main HelpPage ────────────────────────────────────────────────────────────
const HelpPage = () => {
  const navigate = useNavigate();
  const { isOrgAdmin } = useAuth();

  // Default to the tab matching the user's role
  const [tab, setTab] = useState(isOrgAdmin() ? 1 : 0);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <HelpOutline sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
        <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
          Help Center
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Step-by-step guides and answers to common questions
        </Typography>
      </Box>

      {/* Role tabs */}
      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab
            label="Volunteers"
            icon={<VolunteerActivism />}
            iconPosition="start"
            sx={{ py: 2 }}
          />
          <Tab
            label="Organization Admins"
            icon={<AdminPanelSettings />}
            iconPosition="start"
            sx={{ py: 2 }}
          />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {tab === 0 && <VolunteerHelp />}
          {tab === 1 && <OrgAdminHelp />}
        </Box>
      </Paper>

      {/* Still need help? */}
      <Paper
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Still need help?
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
          Our support team is happy to assist with anything not covered here.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/support')}
          sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
        >
          Contact Support
        </Button>
      </Paper>
    </Container>
  );
};

export default HelpPage;
